import React, { Component } from "react"
import PropTypes from "prop-types"
import moment from "moment"

import { isSameDay, formatNumberForDisplay, equivalentArrays } from "shared/imports/sharedHelperFunctions"

import { QUORUM_GREY_1, QUORUM_BLUE } from "shared/imports/sharedConstants"

export default class LineGraph extends Component {
    static propTypes = {
        svgId: PropTypes.string.isRequired,
        isLoading: PropTypes.bool.isRequired,
        colorConfigs: PropTypes.object,
        // line graph data and responses
        data: PropTypes.array,
        storedData: PropTypes.array,
        updateGraph: PropTypes.func.isRequired,
        // line graph specs
        startDate: PropTypes.object, // will be undefined until we get data
        endDate: PropTypes.object, // will be undefined until we get data
        maxCount: PropTypes.number.isRequired,
        graphDerivedData: PropTypes.array.isRequired,
    }

    static defaultProps = {
        minDays: 2,
        colorConfigs: {
            defaultLineColor: QUORUM_BLUE,
            defaultLineBodyColor: QUORUM_GREY_1, // QUORUM_GREY_1
            storedLineColors: ["blue", "green", "orange", "purple", "teal", "grey", "maroon", "black", "red"],
        },
    }

    constructor(props) {
        super(props)
        this.changeDatesOnBrush = this.changeDatesOnBrush.bind(this)
        this.onMouseMove = this.onMouseMove.bind(this)
    }

    componentDidMount() {
        this.setupGraph()
    }

    componentDidUpdate(oldProps) {
        const loadingFinished = oldProps.isLoading && !this.props.isLoading
        const newDateBounds =
            !isSameDay(oldProps.startDate, this.props.startDate) || !isSameDay(oldProps.endDate, this.props.endDate)
        const newCountBounds = oldProps.maxCount !== this.props.maxCount
        const storedLinesChanged = !equivalentArrays(
            oldProps.storedData.map((stored) => stored.key),
            this.props.storedData.map((stored) => stored.key),
        )

        if (loadingFinished) {
            this.clearGraph()
            this.applyData()
        } else if (newDateBounds || newCountBounds) {
            this.updateRange()
        }

        if (storedLinesChanged) {
            this.renderLines()
        }
    }

    componentWillUnmount() {
        d3.select(".d3-tip").remove()
    }

    setupGraph() {
        this.svgWidth = parseInt(d3.select(this.node).style("width"))
        this.svgHeight = parseInt(d3.select(this.node).style("height"))

        d3.select(this.node).attr("width", `${this.svgWidth}px`).attr("height", `${this.svgHeight}px`)

        this.colorArray = [this.props.colorConfigs.defaultLineColor, ...this.props.colorConfigs.storedLineColors]

        d3.selectAll(".d3-tip").remove()

        // tooltip instantiation!
        this.tooltip = d3
            .tip()
            .attr("class", "d3-tip")
            .direction("ne")
            .html((d) => {
                const hasMultipleLines = Object.keys(d.storedLineDetails).length > 0

                let baseText =
                    !isNaN(d.currentLineCount) && !hasMultipleLines
                        ? `<div>${d.dateString}: ${d.currentLineCount}</div>`
                        : `<div>${d.dateString}</div><hr>`

                if (!isNaN(d.currentLineCount) && hasMultipleLines && this.props.showCurrentSearchInLegend) {
                    baseText = baseText.concat(`<div>Current Search: ${d.currentLineCount}</div>`)
                }

                const storedLineText = Object.keys(d.storedLineDetails)
                    .map(
                        (lineName, index) =>
                            `<div style='color: ${this.props.colorConfigs.storedLineColors[index]};'>
                        ${lineName}: ${d.storedLineDetails[lineName]}
                    </div>`,
                    )
                    .join("")

                return storedLineText ? baseText.concat(storedLineText) : baseText
            })

        d3.select(this.node).append("circle").attr("id", "tip-follows-cursor")

        d3.select(this.node).call(this.tooltip)

        d3.select(this.node).on("mousemove", this.onMouseMove).on("mouseout", this.onMouseOut)
    }

    clearGraph() {
        d3.select(this.node).selectAll("g").remove()
    }

    onMouseMove() {
        // if there are no data points in the graph (the user zoomed in too much),
        // don't do anything on move.
        if (!this.props.graphDerivedData.length) {
            return
        }

        const svgNode = d3.select(this.node).node()
        const [mousePositionX] = d3.mouse(svgNode)

        // if we're not within the graph area, do nothing.
        if (
            !mousePositionX ||
            mousePositionX < this.yAxisLabelWidth ||
            mousePositionX > this.svgWidth - this.yAxisLabelWidth / 2
        ) {
            return
        }

        const mouseCircle = d3
            .select("#tip-follows-cursor")
            .attr("cx", d3.event.offsetX + 20)
            .attr("cy", d3.event.offsetY - 20)

        const mouseDate = this.xScale.invert(mousePositionX)

        const bisectDate = d3.bisector((d) => d.date).left
        const index = bisectDate(this.props.graphDerivedData, mouseDate)

        const leftElement = index ? this.props.graphDerivedData[index - 1] : this.props.graphDerivedData[index]

        const rightElement =
            index !== this.props.graphDerivedData.length
                ? this.props.graphDerivedData[index]
                : this.props.graphDerivedData[index - 1]

        const timeDiffLeft = Math.abs(mouseDate - leftElement.date)
        const timeDiffRight = Math.abs(mouseDate - rightElement.date)

        const closestElement = timeDiffLeft < timeDiffRight ? leftElement : rightElement

        this.tooltip.show(closestElement, mouseCircle.node())

        d3.select(this.node).select(".hover-line").style("visibility", "visible")

        d3.select(".hover-line")
            .select("line")
            .attr("x1", this.xScale(closestElement.date))
            .attr("x2", this.xScale(closestElement.date))
            .attr("y1", this.yScale(closestElement.maxCount))
            .attr("y2", this.svgHeight - this.xAxisLabelWidth)
    }

    onMouseOut = () => {
        this.tooltip.hide()

        d3.select(this.node).select(".hover-line").style("visibility", "hidden")
    }

    getXAxisLabelWidth() {
        // we're hardcoding this: this is the width of the longest
        // possible xAxisTick, which is "September". We can't do things
        // based off axis label width, since axis labels change in
        // the x axis when the scale is time.
        return 77
    }

    getYAxisLabelWidth() {
        const labels = d3
            .select(this.node)
            .append("g")
            .attr("class", "test-axis")
            .selectAll("test-x-axis-labels")
            .data(this.props.data)

        let maxWidth = 0

        labels
            .enter()
            .append("text")
            .attr("class", "tick test-axis test-y-axis-labels")
            .attr("x", 0)
            .attr("y", 200)
            .text((d) => formatNumberForDisplay(d))
            .style("text-anchor", "end")
            .attr("visibility", "hidden")
            .attr("text-anchor", "end")
            .each(function () {
                maxWidth = Math.max(this.getBBox().width, maxWidth)
            })

        d3.select(this.node).selectAll(".test-y-axis-labels").remove()
        d3.select(this.node).selectAll(".test-axis").remove()

        // ~accounts for the 6px line, and left padding of 20px~
        // This used to be 26px, but that is no longer enough for some reason, and most numbers were being cut off.
        const padding = 52
        return maxWidth + padding
    }

    changeDatesOnBrush() {
        const [d1, d2] = this.brush.extent()
        d3.selectAll(".brush").call(this.brush.clear())

        const startDate = moment(d1)
        const endDate = moment(d2)

        if (endDate.diff(startDate, "days") > this.props.minDays) {
            this.props.updateGraph(d1, d2)
        }
    }

    updateRange() {
        d3.select(this.node).select(".hover-line").style("visibility", "hidden")

        this.xScale.domain([this.props.startDate, this.props.endDate])
        this.yScale.domain([0, this.props.maxCount])

        d3.select(this.node)
            .select(".x")
            .transition()
            .duration(1000)
            .call(this.xAxis)
            .selectAll("text")
            .attr("transform", "rotate(-45)")
            .style("text-anchor", "end")
            .attr("dx", "-0.75em")
            .attr("dy", "0.5em")

        d3.select(this.node).select(".y").transition().duration(1000).call(this.yAxis)

        d3.select(this.node).select(".line-group").remove()

        this.renderLines(true)
    }

    renderLines(resetLines = false) {
        if (resetLines) {
            d3.select(this.node).selectAll(".line-group").remove()
        }

        // define line and area generating functions
        const lineGenerator = d3.svg
            .line()
            .x((d) => this.xScale(d.date))
            .y((d) => this.yScale(d.count))

        const areaGenerator = d3.svg
            .area()
            .x((d) => this.xScale(d.date))
            .y0(this.svgHeight - this.xAxisLabelWidth)
            .y1((d) => this.yScale(d.count))

        // assembling the data! For visualization purposes,
        // our current search is just another line that'll be rendered
        // with the search.
        const allLines = [
            { key: "Current Search", data: this.props.data, order: 0 }, // current line
            ...this.props.storedData, // stored lines
        ]

        const lines = d3
            .select(this.node)
            .select(".line-groups")
            .selectAll(".line-group")
            .data(allLines, (d) => d.key) // make sure that it has a key

        const baseLines = lines
            .enter()
            .append("g")
            .attr("class", "line-group")
            .append("path")
            .attr("class", "line")
            .style("stroke", (d) => this.colorArray[d.order])
            .style("stroke-width", 3)
            .attr("fill", "none")
            .attr("d", (d) => lineGenerator(d.data))

        // because d3 function 'this' is different from
        // LineGraph this, we need to pull xScale and yScale
        // into a scope that d3 functions can still access.
        const xScale = this.xScale
        const yScale = this.yScale

        const colorScheme = this.colorArray

        // // line animation, because why not?
        const animationDuration = 500

        baseLines.each(function () {
            const pathLength = this.getTotalLength()

            d3.select(this)
                .attr("stroke-dasharray", `${pathLength} ${pathLength}`)
                .attr("stroke-dashoffset", pathLength)
                .transition()
                .duration(animationDuration)
                .ease("linear")
                .attr("stroke-dashoffset", 0)
        })

        // Show as many ~15px circles as possible!
        const numCircles = (this.svgWidth - 1.5 * this.yAxisLabelWidth) / 15

        if (this.props.graphDerivedData.length < numCircles) {
            lines.each(function (d) {
                const strokeColor = colorScheme[d.order]

                if (d3.select(this).select(".circles").empty()) {
                    d3.select(this)
                        .append("g")
                        .attr("class", "circles")
                        .selectAll("circle")
                        .data(d.data)
                        .enter()
                        .append("circle")
                        .attr("stroke", strokeColor)
                        .attr("stroke-width", 0)
                        .attr("fill", "white")
                        .attr("r", 5)
                        .attr("cx", (d) => xScale(d.date))
                        .attr("cy", (d) => yScale(d.count))
                        .transition()
                        .delay((_, i) => (animationDuration / d.data.length) * i)
                        .attr("stroke-width", 5)
                }
            })
        } else if (!this.props.storedData.length) {
            lines
                .append("path")
                .attr("class", "area")
                .attr("fill", "white")
                .attr("d", areaGenerator(this.props.data))
                .transition()
                .delay(animationDuration / 2)
                .duration(animationDuration / 2)
                .attr("fill", this.props.colorConfigs.defaultLineBodyColor)
        }

        lines.exit().remove()

        // must keep the hover line above the fill at all times! Thus we must redraw
        // it every time a new line is rendered.
        d3.select(this.node).select(".hover-line").remove()

        d3.select(this.node)
            .append("g")
            .attr("class", "hover-line")
            .append("line")
            .attr("class", "y")
            .style("stroke", this.props.colorConfigs.defaultLineColor)
            .attr("stroke-width", "2")

        this.renderLegend(resetLines)
    }

    renderLegend(resetLegend = false) {
        if (resetLegend) {
            d3.select(".legend-group").selectAll("rect").remove()
            d3.select(".legend-group").selectAll("text").remove()
        }

        let allKeys = []

        if (this.props.storedData.length) {
            const keys = this.props.storedData.map((datum) => ({
                key: datum.key,
                order: datum.order,
            }))

            allKeys = this.props.showCurrentSearchInLegend ? [{ key: "Current Search", order: 0 }, ...keys] : keys
        }

        const legendGroup = d3.select(".legend-group")

        const legendRects = legendGroup.selectAll("rect").data(allKeys, (d) => d.key)
        const legendTexts = legendGroup.selectAll("text").data(allKeys, (d) => d.key)

        legendRects.transition().attr("y", (_, i) => i * 20 + this.xAxisLabelWidth / 2)

        legendTexts.transition().attr("y", (_, i) => i * 20 + this.xAxisLabelWidth / 2)

        legendRects
            .enter()
            .append("rect")
            .attr("class", "legend-rect")
            .attr("x", this.yAxisLabelWidth + 10)
            .attr("y", (_, i) => i * 20 + this.xAxisLabelWidth / 2)
            .attr("width", 15)
            .attr("height", 15)
            .attr("fill", (d) => this.colorArray[d.order])

        legendTexts
            .enter()
            .append("text")
            .attr("class", "legend-text")
            .attr("x", this.yAxisLabelWidth + 30)
            .attr("y", (_, i) => i * 20 + this.xAxisLabelWidth / 2)
            .attr("fill", (d) => this.colorArray[d.order])
            .attr("dy", ".9em")
            .attr("text-anchor", "start")
            .text((d) => d.key)

        legendRects.exit().remove()
        legendTexts.exit().remove()
    }

    applyData() {
        this.xAxisLabelWidth = this.getXAxisLabelWidth()
        this.yAxisLabelWidth = this.getYAxisLabelWidth()

        this.xScale = d3.time.scale().rangeRound([this.yAxisLabelWidth, this.svgWidth - this.yAxisLabelWidth / 2])
        this.yScale = d3.scale.linear().range([this.svgHeight - this.xAxisLabelWidth, this.xAxisLabelWidth / 2])

        this.xScale.domain([this.props.startDate, this.props.endDate])
        this.yScale.domain([0, this.props.maxCount])

        // make all the groups for our graph!
        d3.select(this.node).append("g").attr("class", "line-groups")

        d3.select(this.node).append("g").attr("class", "legend-group")

        // generate axes!
        this.xAxis = d3.svg.axis().scale(this.xScale).orient("bottom")

        this.yAxis = d3.svg
            .axis()
            .scale(this.yScale)
            .orient("left")
            .ticks(5)
            .tickFormat((d) => formatNumberForDisplay(d))

        // call the axes!
        d3.select(this.node)
            .append("g")
            .attr("class", "x axis")
            .attr("transform", `translate(0,${this.svgHeight - this.xAxisLabelWidth})`)
            .transition()
            .duration(1000)
            .call(this.xAxis)
            .selectAll("text")
            .attr("transform", "rotate(-45)")
            .style("text-anchor", "end")
            .attr("dx", "-0.75em")
            .attr("dy", "0.5em")

        d3.select(this.node)
            .append("g")
            .attr("class", "y axis")
            .attr("transform", `translate(${this.yAxisLabelWidth},0)`)
            .transition()
            .duration(1000)
            .call(this.yAxis)

        // tooltip instantiation!
        const tooltip = d3
            .tip()
            .attr("class", "d3-tip")
            .html((d) => {
                return `${d.date}: ${d.count}`
            })

        d3.select(this.node).call(tooltip)

        // brush instantiation
        this.brush = d3.svg.brush().x(this.xScale).on("brushend", this.changeDatesOnBrush)

        d3.select(this.node)
            .append("g")
            .attr("class", "brush")
            .call(this.brush)
            .selectAll("rect")
            .attr("transform", `translate(0,${this.xAxisLabelWidth / 2})`)
            .attr("height", this.svgHeight - this.xAxisLabelWidth - this.xAxisLabelWidth / 2)

        this.renderLines(this.xAxisLabelWidth)
    }

    render() {
        return <svg ref={(line) => (this.node = line)} id={this.props.svgId} />
    }
}
